import { useCallback, useMemo, useState } from 'react'
import { useToastState } from 'transfix-ui/components/Toast'
import { ISourcedCarrierRate, ISourcedCarrierRateRateSourceEnum } from 'types/graphqlTypes'
import { SourcedCarrierRateObject } from 'apps/marketplace/apiTypes/sourcedCarrierRates'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { AwardedLaneModalTab } from './constants'
import { useDeleteSourcedCarrierRateMutation } from './graphql/DeleteSourcedCarrierRate'
import { SourcedCarrierRatesQueryResult } from './graphql/SourcedCarrierRates'

export const useAwardedLaneModalTabs = () => {
  const [activeTab, setActiveTab] = useState<AwardedLaneModalTab>(AwardedLaneModalTab.Rates)

  const handleTabChange = useCallback((tab: AwardedLaneModalTab) => setActiveTab(tab), [])

  return {
    activeTab,
    handleTabChange,
  }
}

type TSourcedCarrierRate = ISourcedCarrierRate | SourcedCarrierRateObject

export const useFilteredSourcedCarrierRates = <T extends TSourcedCarrierRate>(
  sourcedCarrierRates: T[]
): { fmsRates: T[]; nonFmsRates: T[] } =>
  useMemo(
    () => ({
      fmsRates: sourcedCarrierRates.filter(
        scr => scr.rateSource === ISourcedCarrierRateRateSourceEnum.FMS
      ),
      nonFmsRates: sourcedCarrierRates.filter(
        scr => scr.rateSource !== ISourcedCarrierRateRateSourceEnum.FMS
      ),
    }),
    [sourcedCarrierRates]
  )

export interface IUseDeleteSourcedCarrierRateResult {
  deleteSourcedCarrierRateLoading: boolean
  handleDeleteSourcedCarrierRate: () => Promise<void>
  modalState: {
    closeModal: () => void
    openModal: (deleteId: string) => void
    isModalOpen: boolean
  }
  toastState: Omit<ReturnType<typeof useToastState>, 'openToast'>
}

export const useDeleteSourcedCarrierRate = (
  refetch: SourcedCarrierRatesQueryResult['refetch']
): IUseDeleteSourcedCarrierRateResult => {
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const { openToast, ...restToastState } = useToastState()
  const { trackEvent } = useMPAnalytics()

  const [
    deleteSourcedCarrierRate,
    { loading: deleteSourcedCarrierRateLoading },
  ] = useDeleteSourcedCarrierRateMutation()

  const closeModal = useCallback(() => {
    setDeleteId(null)
  }, [])

  const openModal = useCallback(
    (sourcedCarrierRateId: string) => {
      trackEvent('RFP Lanes', 'CLICKED_DELETE_RATE', { id: sourcedCarrierRateId })
      setDeleteId(sourcedCarrierRateId)
    },
    [trackEvent]
  )

  const handleDeleteSourcedCarrierRate = useCallback(async () => {
    if (!deleteId) {
      return
    }

    try {
      const { data } = await deleteSourcedCarrierRate({
        variables: {
          id: deleteId,
        },
      })

      const { errors, success } = data?.deleteSourcedCarrierRate ?? {}

      if (errors && errors?.length > 0) {
        openToast({
          toastMessage: errors.map(error => error.message).join(', '),
          toastType: 'alert',
        })
      } else if (success) {
        openToast({
          toastMessage: 'Deleted sourced rate sucessfully.',
          toastType: 'default',
        })
      }

      await refetch()
    } catch (err) {
      openToast({
        toastMessage: err?.message,
        toastType: 'alert',
      })
    }

    closeModal()
  }, [closeModal, deleteId, deleteSourcedCarrierRate, openToast, refetch])

  return {
    deleteSourcedCarrierRateLoading,
    handleDeleteSourcedCarrierRate,
    modalState: {
      closeModal,
      openModal,
      isModalOpen: Boolean(deleteId),
    },
    toastState: restToastState,
  }
}
